import {
  applyTextEdits,
  type LintOptions,
  type LintReport,
  type TextEdit,
  utf8ByteLength,
} from '@jsonfmt/core';

import type { AnalyzerService } from '@/services/types';

import type { DocumentSnapshot, SizeBand } from '../types';

/**
 * THE SOURCE OF TRUTH FOR THE TEXT (ADR-0006, ADR-0016, docs/ARCHITECTURE.md §4.8).
 *
 * A plain class. No React, no module singleton, no `setTimeout` reachable from the outside,
 * and no boolean flag anywhere. The legacy build kept the document in React state AND in the
 * editor buffer and reconciled them inside a re-entrant `useEffect` muzzled by
 * `skipValidation` / `isUpdatingFromTree`, reading the editor back through
 * `setTimeout(…, 10)`. All of that was one defect wearing three costumes: two owners for one
 * string. Here there is one owner, and CodeMirror reconciles against `version`.
 *
 * INSTANTIABLE ON PURPOSE. `new DocumentStore(deps)` twice is what makes the Diff View of
 * SPEC.md §6.5 `runCommand(cmd, ctx, storeB)` rather than a rewrite of every command, and it
 * is why feature tests need no `resetStore()` in `beforeEach` and cannot fail in an
 * order-dependent way.
 *
 * ONE ACTION IS ONE UNDO STEP, and that is a property of this file: every mutator produces
 * at most ONE version bump, so `CodeMirrorHost` sends at most one `changes` transaction, so
 * `Mod-Z` after Format restores the original in a single press.
 *
 * ═══ THE FOUR RULES THE TESTS PIN DOWN ═══
 *
 * 1. `setFromEditor` is a NO-OP when the text is unchanged. The echo (store → editor →
 *    store) dies here, at the lowest point, instead of being filtered by a flag upstairs.
 * 2. THE HARD LIMIT NEVER DESTROYS THE PREVIOUS DOCUMENT. `replace` above the limit returns
 *    `{ ok: false, reason: 'too-large' }` and changes nothing. The legacy overwrote the
 *    user's text with `{"error": "File too large..."}`; that is the named regression test.
 * 3. `version` is MONOTONIC, and a report carries the `reportVersion` it describes.
 *    `reportVersion < version` means "this report is not about this text" — staleness is a
 *    comparison, not a race (ADR-0009).
 * 4. A lint answer that arrives out of order is DROPPED. The analyzer's `seq` protection
 *    covers only a lint that was still queued; one already in flight resolves with its own
 *    report, so the store does its own `version > reportVersion` check and does not rely on
 *    the service for correctness.
 */

// ── deps ─────────────────────────────────────────────────────────────────────────────────

/**
 * The two timer functions, as an interface.
 *
 * `tsconfig.core.json` compiles this file with `lib: ["ES2022"]` and `types: []`, so
 * `setTimeout` DOES NOT EXIST as a name here — that is gate 1 working, not an obstacle to
 * route around. Reading the two functions off `globalThis` through this interface keeps the
 * module DOM-free and, far more usefully, lets a test inject a hand-cranked clock: every
 * debounce assertion in `documentStore.test.ts` is deterministic, with no fake timers and no
 * `await new Promise(setTimeout)` anywhere.
 */
export interface TimerApi {
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const globalTimers = globalThis as unknown as TimerApi;

export type ReplaceOutcome =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly reason: 'too-large';
      readonly bytes: number;
      readonly limit: number;
    };

export interface DocumentStoreDeps {
  readonly analyzer: AnalyzerService;
  /** 2 MiB. Above it the UI degrades; the document still works. */
  readonly softLimitBytes: number;
  /** 10 MiB. Above it `replace` REFUSES and the previous text survives untouched. */
  readonly hardLimitBytes: number;
  readonly debounceMsFor: (bytes: number) => number;
  /** Seeds the store without a version bump: the initial snapshot is version 0. */
  readonly initialText?: string;
  readonly lintOptions?: Partial<LintOptions>;
  /**
   * "A newer analysis already owns the answer" — `isSuperseded` from `@/services`.
   *
   * INJECTED rather than imported, because a feature may not import the `@/services` barrel
   * (gate 2, ADR-0015): the barrel reaches `navigator` and `Worker`, and importing it from
   * a file inside `tsconfig.core.json`'s DOM-free program fails gate 1 with a `TS2304`
   * reported against this file, which is not where the problem would be. The composition
   * root passes the real guard in; the default below recognises the same error structurally
   * so a store built bare (every unit test) behaves identically.
   */
  readonly isSuperseded?: (error: unknown) => boolean;
  /**
   * Where a lint failure that is NOT a supersession goes. Default: dropped.
   *
   * There is no `console` in this program either, and a store must not decide to render
   * anything — the composition root wires this to a toast if it wants one.
   */
  readonly onLintError?: (error: unknown) => void;
  readonly timers?: TimerApi;
}

/** Structural twin of `isSuperseded` from `@/services`. See `DocumentStoreDeps.isSuperseded`. */
function looksSuperseded(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as { readonly name?: unknown; readonly reason?: unknown };
  return candidate.name === 'AnalyzerError' && candidate.reason === 'superseded';
}

/** Which band `bytes` falls into. Exported because the size banner and the tests share it. */
export function sizeBandFor(bytes: number, softLimit: number, hardLimit: number): SizeBand {
  if (bytes > hardLimit) return 'over-limit';
  if (bytes >= softLimit) return 'large';
  return 'normal';
}

// ── the store ────────────────────────────────────────────────────────────────────────────

export class DocumentStore {
  readonly #analyzer: AnalyzerService;
  readonly #softLimitBytes: number;
  readonly #hardLimitBytes: number;
  readonly #debounceMsFor: (bytes: number) => number;
  readonly #lintOptions: Partial<LintOptions> | undefined;
  readonly #isSuperseded: (error: unknown) => boolean;
  readonly #onLintError: (error: unknown) => void;
  readonly #timers: TimerApi;

  readonly #listeners = new Set<() => void>();

  #text: string;
  #version = 0;
  #bytes: number;
  #size: SizeBand;
  #report: LintReport | null = null;
  #reportVersion = 0;

  /** Lints dispatched and not yet settled. Two can overlap: one in flight, one queued. */
  #outstanding = 0;
  #timer: unknown = null;
  #disposed = false;
  #snapshot: DocumentSnapshot;

  constructor(deps: DocumentStoreDeps) {
    this.#analyzer = deps.analyzer;
    this.#softLimitBytes = deps.softLimitBytes;
    this.#hardLimitBytes = deps.hardLimitBytes;
    this.#debounceMsFor = deps.debounceMsFor;
    this.#lintOptions = deps.lintOptions;
    this.#isSuperseded = deps.isSuperseded ?? looksSuperseded;
    this.#onLintError = deps.onLintError ?? (() => undefined);
    this.#timers = deps.timers ?? globalTimers;

    this.#text = deps.initialText ?? '';
    this.#bytes = utf8ByteLength(this.#text);
    this.#size = sizeBandFor(this.#bytes, this.#softLimitBytes, this.#hardLimitBytes);
    this.#snapshot = this.#build();
  }

  // ── reading ────────────────────────────────────────────────────────────────────────────

  /**
   * `useSyncExternalStore` contract: the returned function unsubscribes, and `getSnapshot`
   * returns the SAME reference until something observable actually changes (see `#publish`).
   */
  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  getSnapshot(): DocumentSnapshot {
    return this.#snapshot;
  }

  // ── writing ────────────────────────────────────────────────────────────────────────────

  /**
   * From the editor's own edits. A no-op when the text is unchanged — THIS is where the
   * store → editor → store echo is broken (ADR-0006).
   *
   * No hard-limit check on purpose: this text is ALREADY in the editor's buffer, and
   * refusing it here would leave the two out of step, which is the exact failure mode this
   * class exists to make impossible. Oversized text is accepted, banded `over-limit`, and
   * simply not linted on type; `replace` is the door that can say no.
   */
  setFromEditor(text: string): void {
    if (this.#disposed) return;
    this.#commit(text);
  }

  /** From a command. The only door that enforces the hard limit. */
  replace(text: string): ReplaceOutcome {
    if (this.#disposed) return { ok: true };

    const bytes = utf8ByteLength(text);
    if (bytes > this.#hardLimitBytes) {
      // THE NAMED REGRESSION. Nothing below this line runs: no commit, no version bump, no
      // notification. The document the user already had is exactly as they left it.
      return { ok: false, reason: 'too-large', bytes, limit: this.#hardLimitBytes };
    }

    this.#commit(text);
    return { ok: true };
  }

  /**
   * Point edits — a quick fix today, inline tree editing later.
   *
   * Throws `OverlappingEditsError` / `RangeError` from the core when the edits are not
   * applicable. Deliberately not swallowed: that is a programming error in the caller, and
   * `app/runCommand.ts` turns any throw into one error toast.
   */
  applyEdits(edits: readonly TextEdit[]): ReplaceOutcome {
    if (this.#disposed) return { ok: true };
    return this.replace(applyTextEdits(this.#text, edits));
  }

  /**
   * Analyse now, bypassing the debounce. This is the manual Validate button, which is the
   * only way a document past the hard limit is ever linted.
   *
   * Rejects on a real analyzer failure (the caller has a `catch`); a supersession is
   * impossible here because the pending debounce is cancelled first.
   */
  async lintNow(): Promise<LintReport> {
    if (this.#disposed) throw new Error('DocumentStore: lintNow() after dispose()');
    this.#clearTimer();
    this.#publish();
    return this.#dispatchLint();
  }

  /**
   * Stops the clock and forgets the listeners. Does NOT dispose the analyzer: the service is
   * owned by the composition root and, with a Diff View, shared by two stores.
   */
  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#clearTimer();
    this.#listeners.clear();
  }

  // ── internals ──────────────────────────────────────────────────────────────────────────

  #commit(text: string): void {
    if (text === this.#text) return;

    this.#text = text;
    this.#version += 1;
    this.#bytes = utf8ByteLength(text);
    this.#size = sizeBandFor(this.#bytes, this.#softLimitBytes, this.#hardLimitBytes);
    this.#scheduleLint();
    this.#publish();
  }

  #scheduleLint(): void {
    this.#clearTimer();

    // Past the hard limit typing never schedules an analysis; the user presses Validate.
    if (this.#bytes > this.#hardLimitBytes) return;

    this.#timer = this.#timers.setTimeout(() => {
      this.#timer = null;
      void this.#dispatchLint().catch((error: unknown) => {
        if (!this.#isSuperseded(error)) this.#onLintError(error);
      });
    }, this.#debounceMsFor(this.#bytes));
  }

  #clearTimer(): void {
    if (this.#timer === null) return;
    this.#timers.clearTimeout(this.#timer);
    this.#timer = null;
  }

  async #dispatchLint(): Promise<LintReport> {
    const version = this.#version;
    const source = this.#text;

    this.#outstanding += 1;
    this.#publish();

    try {
      const report = await this.#analyzer.lint(source, this.#lintOptions);

      // Rule 4. `version > reportVersion` is the whole staleness policy: an answer about an
      // older text loses to one already recorded, and an answer about text that has since
      // changed is still recorded — with its own, lower, `reportVersion`, which is what
      // makes the status pill go neutral instead of showing a verdict about the past.
      if (!this.#disposed && version > this.#reportVersion) {
        this.#report = report;
        this.#reportVersion = version;
      }
      return report;
    } finally {
      this.#outstanding -= 1;
      this.#publish();
    }
  }

  #build(): DocumentSnapshot {
    return {
      text: this.#text,
      version: this.#version,
      bytes: this.#bytes,
      size: this.#size,
      report: this.#report,
      reportVersion: this.#reportVersion,
      linting: this.#timer !== null || this.#outstanding > 0,
    };
  }

  /**
   * Rebuild, and notify ONLY if something a subscriber can see actually differs.
   *
   * `useSyncExternalStore` re-renders on every notification and compares snapshots by
   * reference, so handing out a fresh object for an unchanged state would repaint the
   * problems panel on every keystroke (ADR-0016 calls this out as discipline rather than
   * mechanism; this is where the discipline lives).
   */
  #publish(): void {
    const next = this.#build();
    const previous = this.#snapshot;

    if (
      next.text === previous.text &&
      next.version === previous.version &&
      next.bytes === previous.bytes &&
      next.size === previous.size &&
      next.report === previous.report &&
      next.reportVersion === previous.reportVersion &&
      next.linting === previous.linting
    ) {
      return;
    }

    this.#snapshot = next;
    for (const listener of [...this.#listeners]) listener();
  }
}
